/**
 * /admin/leads  —  Phone-friendly leads dashboard for the founder (Feb 14 2026)
 * ============================================================================
 *
 * One page. Three sources. No filters. Auto-refresh every 30s.
 * Designed to be checked from the phone during the hackathon to see
 * "who's interested right now".
 *
 * Backend: GET /api/admin/leads  (admin-only via JWT + email allowlist)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, Mail, MessageSquare, Slack, Sparkles,
  Loader2, AlertTriangle, Inbox, Clock, Zap,
} from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const StatPill = ({ label, value, tone, testId }) => {
  const TONES = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    sky:     'bg-sky-500/10     border-sky-500/30     text-sky-300',
    violet:  'bg-violet-500/10  border-violet-500/30  text-violet-300',
    amber:   'bg-amber-500/10   border-amber-500/30   text-amber-300',
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${TONES[tone] || TONES.emerald} px-4 py-3 flex flex-col gap-1`}
    >
      <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
      <span className="text-2xl font-bold tabular-nums">{value}</span>
    </div>
  );
};

const LeadRow = ({ icon: Icon, accent, primary, secondary, badge, time, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-3 py-3 border-b border-white/5 last:border-b-0"
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-${accent}-500/15 ring-1 ring-${accent}-500/30`}>
      <Icon className={`w-3.5 h-3.5 text-${accent}-300`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white truncate">{primary}</p>
      {secondary && (
        <p className="text-xs text-slate-400 truncate">{secondary}</p>
      )}
    </div>
    <div className="text-right shrink-0">
      {badge && (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-${accent}-500/15 text-${accent}-200 mb-1`}>
          {badge}
        </span>
      )}
      <p className="text-[10px] text-slate-500 flex items-center gap-1 justify-end">
        <Clock className="w-3 h-3" /> {time}
      </p>
    </div>
  </div>
);

const Section = ({ title, count, items, empty, renderRow, testId }) => (
  <section className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testId}>
    <header className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">{title}</h2>
      <span className="text-xs text-slate-400 tabular-nums">{count} total</span>
    </header>
    <div className="px-4">
      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">{empty}</p>
      ) : (
        items.map(renderRow)
      )}
    </div>
  </section>
);

export default function Leads() {
  const [data, setData] = useState(null);
  const [quickstart, setQuickstart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = async () => {
    setErr('');
    try {
      const [leadsRes, qsRes] = await Promise.all([
        axios.get(`${API}/admin/leads?limit=50`, { headers }),
        // Quickstart breakdown is best-effort — never fail the page if it errors.
        axios.get(`${API}/analytics/quickstart-breakdown?days=30`, { headers }).catch(() => null),
      ]);
      setData(leadsRes.data);
      if (qsRes && qsRes.data) setQuickstart(qsRes.data);
      setUpdatedAt(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setErr('Admin access required. Sign in as the founder account first.');
      } else {
        setErr(detail || e.message || 'Could not load leads.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = data?.counts || {};
  const general = data?.general_recent || [];
  const chatbot = data?.chatbot_recent || [];
  const contact = data?.contact_recent || [];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead title="Leads · Admin · CARTIEAI" noindex />

      {/* Sticky header (works great on mobile) */}
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-slate-950/80 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="admin-leads-back">
            <ArrowLeft className="w-3.5 h-3.5" />
            Home
          </Link>
          <div className="text-center">
            <p className="text-sm font-semibold text-white">Leads</p>
            {updatedAt && (
              <p className="text-[10px] text-slate-500" data-testid="admin-leads-updated-at">
                Updated {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="admin-leads-refresh"
            className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Loading */}
        {loading && !data && (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm" data-testid="admin-leads-loading">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading leads…
          </div>
        )}

        {/* Error */}
        {err && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="admin-leads-error">
            <AlertTriangle className="w-4 h-4 text-rose-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-rose-200 font-medium">Couldn't load leads</p>
              <p className="text-xs text-rose-300/80 mt-0.5">{err}</p>
              {(err.toLowerCase().includes('admin') || err.toLowerCase().includes('sign in')) && (
                <Link to="/login" className="inline-block mt-2 text-xs text-emerald-300 underline">
                  Go to sign in →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Stat strip */}
        {data && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="admin-leads-stats">
            <StatPill label="Total" value={counts.total ?? 0} tone="emerald" testId="admin-leads-stat-total" />
            <StatPill label="Beta waitlist" value={counts.general_waitlist ?? 0} tone="amber" testId="admin-leads-stat-beta" />
            <StatPill label="Teams bot" value={counts.chatbot_teams ?? 0} tone="sky" testId="admin-leads-stat-teams" />
            <StatPill label="Slack bot" value={counts.chatbot_slack ?? 0} tone="violet" testId="admin-leads-stat-slack" />
          </section>
        )}

        {/* Quickstart Strip conversions — 30 day rollup of which "first
            integration" pitch is winning on /connect. Hidden until at least
            one click has been recorded. */}
        {quickstart && quickstart.total_clicks > 0 && (
          <section
            className="rounded-2xl border border-white/10 bg-white/[0.02] p-4"
            data-testid="admin-leads-quickstart"
          >
            <header className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-300" />
                <h2 className="text-sm font-semibold text-white">Quickstart conversions</h2>
              </div>
              <span className="text-[10px] text-slate-400 tabular-nums">
                {quickstart.total_clicks} clicks · {quickstart.period_days}d
              </span>
            </header>
            <div className="grid grid-cols-3 gap-2">
              {['slack', 'jira', 'datadog'].map((destId) => {
                const row = quickstart.breakdown.find((b) => b.dest_id === destId) || { clicks: 0, unique_sessions: 0 };
                const pct = quickstart.total_clicks > 0
                  ? Math.round((row.clicks / quickstart.total_clicks) * 100)
                  : 0;
                const tone = destId === 'slack'
                  ? 'from-violet-500/15 border-violet-500/30 text-violet-200'
                  : destId === 'jira'
                  ? 'from-sky-500/15 border-sky-500/30 text-sky-200'
                  : 'from-emerald-500/15 border-emerald-500/30 text-emerald-200';
                return (
                  <div
                    key={destId}
                    className={`rounded-xl border bg-gradient-to-br ${tone} px-3 py-3`}
                    data-testid={`admin-leads-quickstart-${destId}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider opacity-80">{destId}</div>
                    <div className="text-xl font-bold tabular-nums mt-0.5">{row.clicks}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">
                      {pct}% · {row.unique_sessions} {row.unique_sessions === 1 ? 'visitor' : 'visitors'}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* General beta waitlist */}
        {data && (
          <Section
            title="Beta waitlist"
            count={counts.general_waitlist ?? 0}
            items={general}
            empty="No beta-waitlist signups yet."
            testId="admin-leads-section-beta"
            renderRow={(it, idx) => (
              <LeadRow
                key={`gen-${idx}`}
                testId={`admin-lead-beta-${idx}`}
                icon={Sparkles}
                accent="amber"
                primary={it.email}
                secondary={[it.name, it.company, it.cloud_spend].filter(Boolean).join(' · ')}
                badge={it.status}
                time={fmtAgo(it.created_at)}
              />
            )}
          />
        )}

        {/* Chat-bot waitlist (Slack + Teams in one list, badge tells which) */}
        {data && (
          <Section
            title="Chat-bot waitlist"
            count={(counts.chatbot_slack ?? 0) + (counts.chatbot_teams ?? 0)}
            items={chatbot}
            empty="No chat-bot waitlist signups yet."
            testId="admin-leads-section-chatbot"
            renderRow={(it, idx) => {
              const isSlack = it.platform === 'slack';
              return (
                <LeadRow
                  key={`bot-${idx}`}
                  testId={`admin-lead-bot-${idx}`}
                  icon={isSlack ? Slack : MessageSquare}
                  accent={isSlack ? 'violet' : 'sky'}
                  primary={it.email}
                  secondary={[it.company, it.workspace].filter(Boolean).join(' · ')}
                  badge={isSlack ? 'Slack' : 'Teams'}
                  time={fmtAgo(it.created_at)}
                />
              );
            }}
          />
        )}

        {/* Contact-sales leads (optional collection) */}
        {data && (counts.contact_sales ?? 0) > 0 && (
          <Section
            title="Contact-sales leads"
            count={counts.contact_sales ?? 0}
            items={contact}
            empty="No contact-sales leads yet."
            testId="admin-leads-section-contact"
            renderRow={(it, idx) => (
              <LeadRow
                key={`contact-${idx}`}
                testId={`admin-lead-contact-${idx}`}
                icon={Mail}
                accent="emerald"
                primary={it.email}
                secondary={[it.name, it.company, it.message].filter(Boolean).join(' · ')}
                badge="Sales"
                time={fmtAgo(it.created_at)}
              />
            )}
          />
        )}

        {/* When everything is empty */}
        {data && counts.total === 0 && (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl" data-testid="admin-leads-empty">
            <Inbox className="w-7 h-7 mx-auto text-slate-500 mb-2" />
            <p className="text-sm text-slate-400">No leads yet. Tell the world.</p>
          </div>
        )}
      </main>
    </div>
  );
}
